import axios from 'axios';

export interface VastTrackingEvent {
  event: string;
  url: string;
}

export interface VastParseResult {
  mediaUrl: string | null;
  clickThroughUrl: string | null;
  trackingEvents: VastTrackingEvent[];
  isWrapper: boolean;
  error: string | null;
}

function isUrl(str: string): boolean {
  const trimmed = str.trim();
  return trimmed.startsWith('http://') || trimmed.startsWith('https://');
}

export async function fetchAndParseVast(tagContent: string): Promise<VastParseResult> {
  if (isUrl(tagContent)) {
    try {
      const { data } = await axios.get('/creatives/vast-proxy', {
        params: { url: tagContent.trim() },
      });
      return parseVastXml(data);
    } catch {
      return {
        mediaUrl: null,
        clickThroughUrl: null,
        trackingEvents: [],
        isWrapper: false,
        error: 'Failed to fetch VAST tag URL',
      };
    }
  }
  return parseVastXml(tagContent);
}

export function parseVastXml(xml: string): VastParseResult {
  const result: VastParseResult = {
    mediaUrl: null,
    clickThroughUrl: null,
    trackingEvents: [],
    isWrapper: false,
    error: null,
  };

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'text/xml');

    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      result.error = 'Failed to parse VAST XML';
      return result;
    }

    if (doc.querySelector('VASTAdTagURI')) {
      result.isWrapper = true;
    }

    const mediaFile = doc.querySelector('MediaFile');
    if (mediaFile) {
      result.mediaUrl = mediaFile.textContent?.trim() || null;
    }

    const clickThrough = doc.querySelector('ClickThrough');
    if (clickThrough) {
      result.clickThroughUrl = clickThrough.textContent?.trim() || null;
    }

    doc.querySelectorAll('Tracking').forEach(el => {
      const event = el.getAttribute('event');
      const url = el.textContent?.trim();
      if (event && url) {
        result.trackingEvents.push({ event, url });
      }
    });
  } catch {
    result.error = 'Failed to parse VAST XML';
  }

  return result;
}
